import {
    faArchive,
    faBan,
    faBell,
    faBinoculars,
    faBookOpen,
    faCalendarAlt,
    faCashRegister,
    faCheck,
    faCopy,
    faDatabase,
    faDollarSign,
    faEdit,
    faEnvelope,
    faFileExport,
    faFileImport,
    faLink,
    faReceipt,
    faRectangleXmark,
    faSignInAlt,
    faStamp,
    faSync,
    faTrash,
    faTrashRestore
} from '@fortawesome/free-solid-svg-icons';
import { useApi } from '@hooks/useApi';
import { Blockquote, Container } from '@ui';
import { Action, Column, DataTable, Filter, Selection, TableActionButton } from 'components/DataTable';
import { ActionCallbackProps, AsyncListRef, FilterState } from 'components/DataTable/DataTable';
import { useWindowDimensions } from 'hooks';
import { useConfirmedCallback } from 'hooks/useConfirmedCallback';
import { useModal } from 'hooks/useModal';
import dynamic from 'next/dynamic';
import { useCallback, useMemo, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useSWRConfig } from 'swr';
import { useBankAccounts } from '~/components/Banking/shared/hooks/useBankAccounts';
import { useSettings } from '../../Clubs/hooks';
import { useIssuers, usePaymentTerms } from '../data';
import DuplicateMultipleAssistant from '../DuplicateMultipleAssistant';
import useFinalizeInvoices from '../hooks/useFinalizeInvoices';
import useInvoicePath from '../hooks/useInvoicePath';
import InvoiceCreditForm from '../InvoiceCreditForm/InvoiceCreditForm';
import { getSecureMatchSuggestions, MatchSuggestionTooltip } from '../MatchSuggestionTooltip';
import { SetAccountForm } from '../SetAccountForm';
import { UserInvoice } from '../types';
import InvoiceExternalImportForm from './InvoiceExternalImportForm';
import { MetaComponent } from './MetaComponent';
import { NumberComponent } from './NumberComponent';
import { RecipientComponent } from './RecipientComponent';
import { StatusBar } from './StatusBar';
import { useExperimental } from 'hooks/useExperimental';

const OPOSImportAssistant = dynamic(() => import('../OPOSImportAssistant'));
const ImportAssistant = dynamic(() => import('../ImportAssistant'));
const DunningAssistant = dynamic(() => import('../DunningAssistant/DunningAssistant'));
const PaymentManager = dynamic(() => import('../PaymentAssistent/PaymentManager'));
const MultiplePaymentForm = dynamic(() => import('../PaymentAssistent/MultiplePaymentForm'));
const InvoiceForm = dynamic(() => import('../InvoiceForm'));
const MultipleInvoiceForm = dynamic(() => import('../MultipleInvoiceForm'));
const MoveToCategoryForm = dynamic(() => import('../MoveToCategoryForm'));
const SepaReportAssistent = dynamic(() => import('../SepaReportAssistent'));
const SepaPrenotificationAssistent = dynamic(() => import('../SepaPrenotificationAssistent'));
const DeliveryForm = dynamic(() => import('../DeliveryForm'));
const MultipleDunningsForm = dynamic(() => import('../MultipleDunningsForm'));
const RatePaymentForm = dynamic(() => import('../RatePaymentForm'));
const DunningDeliveryForm = dynamic(() => import('../DunningDeliveryForm'));
const CancellationForm = dynamic(() => import('../CancellationForm'));
const PaymentCancellationForm = dynamic(() => import('../PaymentCancellationForm'));
const ResolveInvoicesForm = dynamic(() => import('../ResolveInvoicesForm'));
const FormDataPreview = dynamic(() => import('../../Formular/FormDataPreview'));
const AutoCombineForm = dynamic(() => import('../AutoCombineForm'));
const ExportAssistant = dynamic(() => import('../ExportAssistant'));

interface Props {
    global?: boolean;
    clubSlug: string;
    archive?: boolean;
    fetchUrl?: string;
    asyncListRef?: any;
    className?: string;
}

/** Rechnungsnummer, Empfängername, E-Mail und Zahlungsstatus stehen als Quickfilter über der
 *  Tabelle; DataTable blendet sie dadurch automatisch aus dem "weitere Filter"-Dialog aus. */
const QUICK_FILTER_KEYS = ['number', 'recipient', 'email', 'payment_status', 'sepa_xml_requirements_fulfilled'];

export const InvoiceList = ({ clubSlug, archive, fetchUrl, asyncListRef: _asyncListRef, global, className }: Props) => {
    const api = useApi();
    const { t } = useTranslation('invoice');
    const sepaExperimental = useExperimental().includes('sepa_v1');
    const { data: bankAccounts } = useBankAccounts();
    const paymentTerms = usePaymentTerms(clubSlug);
    const { width } = useWindowDimensions();
    const [setModal, resetModal] = useModal();
    const [currentFilter, setCurrentFilter] = useState<FilterState>();
    const [selection, setSelection] = useState<Selection>();
    const { mutate } = useSWRConfig();
    const [path] = useInvoicePath();
    const asyncListRef = useRef<AsyncListRef>(undefined);
    const [settings, isLoadingSettings] = useSettings('user_invoice', clubSlug);
    const { data: issuerData } = useIssuers(clubSlug);
    const showFirstPosition = settings?.view?.show_first_position;
    const reloadOrUpdate = useCallback(
        (invoice?: UserInvoice | null) =>
            invoice?.id ? asyncListRef.current?.update(invoice.id, invoice) : asyncListRef.current?.reload?.(),
        []
    );
    const [isLoading, setIsLoading] = useState(false);

    const handleShowInvoiceForm = useCallback(
        (invoice?: UserInvoice) =>
            setModal({
                title: invoice ? t('invoiceList.modals.editInvoice') : t('invoiceList.common.createInvoice'),
                content: (
                    <InvoiceForm
                        invoice={invoice}
                        callback={() => {
                            reloadOrUpdate();
                            resetModal();
                        }}
                    />
                ),
                show: true,
                size: 80
            }),
        []
    );

    const handleShipMultipleDunnings = useCallback(
        ({ selection: items, filter }: ActionCallbackProps) => {
            setModal({
                title: t('invoiceList.common.shipDunnings'),
                content: (
                    <DunningDeliveryForm
                        filter={filter}
                        invoices={items}
                        selectedCategory={path}
                        callback={() => {
                            resetModal();
                            reloadOrUpdate();
                        }}
                    />
                ),
                show: true,
                size: 60
            });
        },
        [path]
    );

    const handleCreateMultipleDunnings = useCallback(
        ({ selection: items, filter }: ActionCallbackProps) => {
            setModal({
                title: t('invoiceList.common.shipDunnings'),
                content: (
                    <MultipleDunningsForm
                        filter={filter}
                        invoices={items}
                        selectedCategory={path}
                        callback={() => {
                            resetModal();
                            reloadOrUpdate();
                        }}
                    />
                ),
                show: true
            });
        },
        [path]
    );

    const handleCapturePaymentMultiple = useCallback(
        ({ selection: items, filter }: ActionCallbackProps) => {
            setModal({
                title: t('invoiceList.modals.capturePayments'),
                content: (
                    <MultiplePaymentForm
                        filter={filter}
                        invoices={items}
                        selectedCategory={path}
                        callback={() => {
                            resetModal();
                            reloadOrUpdate();
                        }}
                    />
                ),
                show: true
            });
        },
        [reloadOrUpdate, path]
    );

    const handleShowSepaReportAssistent = ({ selection: items, filter }: ActionCallbackProps) => {
        setModal({
            title: t('invoiceList.modals.sepaAssistant'),
            content: (
                <SepaReportAssistent
                    filter={filter}
                    clubSlug={clubSlug}
                    invoices={items}
                    callback={() => {
                        reloadOrUpdate();
                        resetModal();
                    }}
                />
            ),
            show: true,
            onClose: () => reloadOrUpdate()
        });
    };

    const handleShowSepaPrenotificationAssistent = ({ selection: items, filter }: ActionCallbackProps) => {
        setModal({
            title: t('invoiceList.modals.sepaPrenotification'),
            content: (
                <SepaPrenotificationAssistent
                    filter={filter}
                    invoices={items}
                    callback={() => {
                        reloadOrUpdate();
                        resetModal();
                    }}
                />
            ),
            show: true,
            // Wider than the default 60vw: this one puts a six-column table next to the
            // message form, which does not fit into half a standard modal.
            size: 85,
            onClose: () => reloadOrUpdate()
        });
    };

    const handleShipInvoices = ({ selection: items, filter }: ActionCallbackProps) =>
        setModal({
            title: t('invoiceList.modals.shipInvoices'),
            content: (
                <DeliveryForm filter={filter} clubSlug={clubSlug} invoices={items} callback={() => resetModal()} />
            ),
            show: true,
            size: 100,
            onClose: () => reloadOrUpdate()
        });

    const { isLoading: isFinalizing, finalize } = useFinalizeInvoices(clubSlug, reloadOrUpdate);

    // Which row started the run, so only that row's stamp button spins. A run from the
    // toolbar leaves it undefined and the spinner stays on the toolbar action.
    const [finalizingRowId, setFinalizingRowId] = useState<string>();

    const handleFinalizeMultipleElements = useConfirmedCallback(
        ({ selection: items, filter, rowId }: ActionCallbackProps & { rowId?: string }) => {
            setFinalizingRowId(rowId);
            finalize({ selection: items, filter, selectedCategory: path });
        },
        {
            header: t('invoiceList.actions.finalizeMultiple.confirmHeader'),
            message: ({ selection: items }: ActionCallbackProps) => (
                <>
                    <p className="tw-mb-2 tw-mt-2 tw-font-semibold">
                        {items === 'all'
                            ? t('invoiceList.actions.finalizeMultiple.confirmScopeAll')
                            : t('invoiceList.actions.finalizeMultiple.confirmScopeCount', {
                                  count: items?.length ?? 0
                              })}
                    </p>
                    <Blockquote color="warning" className="!tw-my-0 !tw-mb-4">
                        <Trans
                            t={t}
                            i18nKey="invoiceList.actions.finalizeMultiple.confirmMessage"
                            components={{
                                br: <br />,
                                link: (
                                    <a
                                        target="_blank"
                                        href="https://ao.bundesfinanzministerium.de/ao/2023/Anhaenge/BMF-Schreiben-und-gleichlautende-Laendererlasse/Anhang-64/inhalt.html"
                                        rel="noreferrer"
                                    />
                                )
                            }}
                        />
                    </Blockquote>
                </>
            )
        }
    );

    const handleReconcileOnlinePayments = ({ selection: items, filter }: ActionCallbackProps) => {
        setIsLoading(true);
        api({
            url: `invoice/${clubSlug}/reconcile-payments`,
            method: 'POST',
            data: {
                filter,
                invoices: items,
                selectedCategory: path
            },
            onBefore: () => setIsLoading(false),
            onSuccess: () => reloadOrUpdate()
        });
    };

    const handleResolveMultiple = ({ selection: items, filter }: ActionCallbackProps) => {
        setModal({
            title: t('invoiceList.common.resolveSelected'),
            content: (
                <ResolveInvoicesForm
                    invoices={items}
                    filter={filter}
                    selectedCategory={path}
                    toastCallback={() => reloadOrUpdate()}
                    callback={() => resetModal()}
                />
            ),
            show: true
        });
    };

    const handleDeleteOrArchive = useConfirmedCallback((invoice: UserInvoice) => {
        setIsLoading(true);
        api({
            url: `invoice/${invoice.id}`,
            method: 'DELETE',
            onBefore: () => setIsLoading(false),
            onSuccess: () => {
                asyncListRef.current?.remove(invoice.id);
                resetModal();
            }
        });
    });

    const handleDeleteOrArchiveMultiple = useConfirmedCallback(
        ({ selection: items, setSelection, filter }: ActionCallbackProps) => {
            setIsLoading(true);
            api({
                url: `invoice/${clubSlug}/delete`,
                method: 'POST',
                data: {
                    filter,
                    invoices: items,
                    selectedCategory: path
                },
                onBefore: () => setIsLoading(false),
                onSuccess: () => {
                    reloadOrUpdate();
                    setSelection?.([]);
                }
            });
        },
        {
            message: t('invoiceList.actions.deleteMultiple.confirmMessage')
        }
    );

    const handleRestoreMultiple = useConfirmedCallback(({ selection: items, filter }: ActionCallbackProps) => {
        setIsLoading(true);
        api({
            url: `invoice/${clubSlug}/restore`,
            method: 'POST',
            data: {
                filter,
                invoices: items,
                selectedCategory: path
            },
            onBefore: () => setIsLoading(false),
            onSuccess: () => reloadOrUpdate()
        });
    });

    const handleOpenFormDataPreview = useCallback((invoice: UserInvoice) => {
        setModal({
            title: t('invoiceList.modals.formDataPreview'),
            content: <FormDataPreview formDataId={invoice.form_data_id} />,
            show: true,
            size: 80
        });
    }, []);

    const handleCombineMultiple = useConfirmedCallback(
        ({ selection: items, filter }: ActionCallbackProps) => {
            setIsLoading(true);
            api({
                url: `invoice/${clubSlug}/combine`,
                method: 'POST',
                data: {
                    filter,
                    invoices: items,
                    selectedCategory: path
                },
                onBefore: () => setIsLoading(false),
                onSuccess: ({}) => reloadOrUpdate()
            });
        },
        {
            message: t('invoiceList.actions.combineMultiple.confirmMessage')
        }
    );

    // Opens the ImportAssistant modal which lets the user pick an import format
    const handleShowImportAssistant = () => {
        setModal({
            title: t('invoiceList.modals.importAssistant'),
            content: (
                <ImportAssistant
                    callback={() => {
                        resetModal();
                        reloadOrUpdate();
                        void mutate((key) => typeof key === 'string' && key.startsWith(`invoice/${clubSlug}/category`));
                    }}
                />
            ),
            show: true
        });
    };

    const handleShowExportAssistant = ({ selection, filter }: ActionCallbackProps) => {
        setModal({
            title: t('invoiceList.modals.exportAssistant'),
            content: <ExportAssistant selection={selection} filter={filter} callback={resetModal} />,
            show: true
        });
    };

    const handleImportInvoicePdf = useCallback(() => {
        setModal({
            title: t('invoiceList.common.importInvoicePdf'),
            content: (
                <InvoiceExternalImportForm
                    clubSlug={clubSlug}
                    allowFileUpload
                    data={{}}
                    onSuccess={(invoice) => {
                        reloadOrUpdate(invoice);
                    }}
                    onComplete={() => resetModal()}
                />
            ),
            show: true,
            size: 90
        });
    }, [clubSlug, reloadOrUpdate, resetModal, setModal]);

    const handleEditMultipleElements = ({ selection: items, filter }: ActionCallbackProps) => {
        setModal({
            title: t('invoiceList.modals.editMultiple'),
            content: (
                <MultipleInvoiceForm
                    filter={filter}
                    invoices={items}
                    clubSlug={clubSlug}
                    callback={() => {
                        reloadOrUpdate();
                        resetModal();
                    }}
                />
            ),
            show: true,
            size: 90
        });
    };

    const handleSetAccountMultiple = ({ selection: items, filter }: ActionCallbackProps) => {
        setModal({
            title: t('invoiceList.modals.setAccountMultiple'),
            content: (
                <SetAccountForm
                    filter={filter}
                    invoices={items}
                    selectedCategory={path}
                    clubSlug={clubSlug}
                    callback={() => {
                        reloadOrUpdate();
                        resetModal();
                    }}
                />
            ),
            show: true,
            size: 50
        });
    };

    const handleRatePaymentMultiple = ({ selection, filter }: ActionCallbackProps) => {
        setModal({
            title: t('invoiceList.modals.ratePaymentMultiple'),
            content: (
                <RatePaymentForm
                    filter={filter}
                    selection={selection}
                    selectedCategory={path}
                    clubSlug={clubSlug}
                    callback={() => {
                        reloadOrUpdate();
                        resetModal();
                    }}
                />
            ),
            show: true
        });
    };

    const handleShowInvoiceCreditForm = (invoice: UserInvoice) => {
        setModal({
            title: t('invoiceList.common.creditInvoice'),
            content: (
                <InvoiceCreditForm
                    invoice={invoice}
                    callback={() => {
                        reloadOrUpdate();
                        resetModal();
                    }}
                />
            ),
            show: true,
            size: 80
        });
    };

    const handleDuplicateMultipleElements = ({ selection: items, filter }: ActionCallbackProps) => {
        setModal({
            title: t('invoiceList.modals.duplicateMultiple.title'),
            content: (
                <DuplicateMultipleAssistant
                    filter={filter}
                    invoices={items}
                    clubSlug={clubSlug}
                    callback={() => {
                        alert(t('invoiceList.modals.duplicateMultiple.alert'));
                        resetModal();
                    }}
                />
            ),
            show: true
        });
    };

    const handleMoveMultiple = ({ selection: items, filter }: ActionCallbackProps) => {
        setModal({
            title: t('invoiceList.common.moveSelected'),
            content: (
                <div className="tw-h-[60vh]">
                    <MoveToCategoryForm
                        invoices={items}
                        filter={filter}
                        callback={() => {
                            resetModal();
                            reloadOrUpdate();
                        }}
                    />
                </div>
            ),
            show: true
        });
    };

    const handleCancelMultiple = ({ selection: items, filter }: ActionCallbackProps) => {
        setModal({
            title: t('invoiceList.common.cancelSelected'),
            content: (
                <CancellationForm
                    invoices={items}
                    filter={filter}
                    selectedCategory={path}
                    callback={() => {
                        resetModal();
                        reloadOrUpdate();
                    }}
                />
            ),
            show: true
        });
    };

    const handleCancelPaymentMultiple = ({ selection: items, filter }: ActionCallbackProps) => {
        setModal({
            title: t('invoiceList.common.cancelPaymentSelected'),
            content: (
                <PaymentCancellationForm
                    invoices={items}
                    filter={filter}
                    selectedCategory={path}
                    callback={() => {
                        resetModal();
                        reloadOrUpdate();
                    }}
                />
            ),
            show: true
        });
    };

    const handleUndoCancelMultiple = useConfirmedCallback(
        ({ selection: items, filter }: ActionCallbackProps) => {
            setIsLoading(true);
            api({
                url: `invoice/${clubSlug}/undo-cancel`,
                method: 'POST',
                data: { filter, selectedCategory: path, invoices: items },
                onSuccess: () => reloadOrUpdate(),
                onBefore: () => setIsLoading(false)
            });
        },
        {
            header: t('invoiceList.actions.undoCancelMultiple.confirmHeader'),
            message: t('invoiceList.actions.undoCancelMultiple.confirmMessage')
        }
    );

    const handleOpenDunningAssistent = useCallback(
        (invoice: UserInvoice) =>
            setModal({
                title: t('invoiceList.common.manageDunnings'),
                content: (
                    <DunningAssistant
                        invoice={invoice}
                        callback={() => {
                            asyncListRef.current?.reload();
                        }}
                    />
                ),
                show: true,
                onClose: () => {
                    asyncListRef.current?.reload();
                }
            }),
        []
    );

    const handleManagePayment = useCallback(
        (invoice: UserInvoice, initialTab?: 'manual' | 'bank') => {
            const openManager = (currentInvoice: UserInvoice) =>
                setModal({
                    title: t('invoiceList.modals.capturePayment'),
                    content: (
                        <PaymentManager
                            clubSlug={clubSlug}
                            invoice={currentInvoice}
                            paymentBasePath="invoice"
                            hasBankAccounts={bankAccounts.length > 0}
                            bankTransactions={currentInvoice.bank_transactions ?? []}
                            invoiceValue={currentInvoice.total_cents ?? null}
                            invoiceDebtor={currentInvoice.line0 ?? null}
                            invoiceIban={currentInvoice.iban ?? null}
                            invoiceNumber={currentInvoice.number ?? null}
                            invoiceDate={currentInvoice.date ?? null}
                            invoiceRemarks={currentInvoice.purpose ?? null}
                            invoiceRatesCount={currentInvoice.rates?.length ?? null}
                            invoiceRateDates={currentInvoice.rates?.map((r: { date: string }) => r.date) ?? null}
                            initialTab={initialTab}
                            onBankTransactionsChange={(updatedData) => {
                                asyncListRef.current?.update(currentInvoice.id, {
                                    ...currentInvoice,
                                    ...(updatedData as Partial<UserInvoice>)
                                });
                            }}
                            callback={() => reloadOrUpdate()}
                            onPaymentRecorded={() => {
                                reloadOrUpdate();
                                resetModal();
                            }}
                        />
                    ),
                    show: true,
                    size: 90,
                    onClose: () => reloadOrUpdate()
                });

            void api({
                url: `invoice/${invoice.id}`,
                method: 'GET',
                muted: true,
                onSuccess: (response) => {
                    openManager((response?.data as UserInvoice | null) ?? invoice);
                },
                onError: () => {
                    openManager(invoice);
                }
            });
        },
        [api, clubSlug, bankAccounts, setModal, resetModal]
    );

    const handleAutoCombineInvoices = useCallback(
        ({ selection: items, filter }: ActionCallbackProps) => {
            setModal({
                title: t('invoiceList.modals.autoCombine'),
                content: (
                    <AutoCombineForm
                        items={items}
                        filter={filter}
                        clubSlug={clubSlug}
                        path={path}
                        toastCallback={reloadOrUpdate}
                        callback={resetModal}
                    />
                ),
                show: true
            });
        },
        [clubSlug, path]
    );

    const handleShowPDF = (invoice: UserInvoice) => {
        let popup = window.open(invoice.url, '_blank');
        if (popup != null) popup.focus();
    };

    const handleDuplicate = useConfirmedCallback((invoice: UserInvoice) =>
        api({
            url: 'invoice/duplicate/' + invoice.id,
            onSuccess: () => reloadOrUpdate()
        })
    );

    const columns: Column<UserInvoice>[] = useMemo(
        () => [
            {
                uid: 'number',
                name: t('invoiceList.columns.number'),
                allowsSorting: true,
                renderer: (_, item) => <NumberComponent showFirstPosition={Boolean(showFirstPosition)} invoice={item} />
            },
            {
                uid: 'line0',
                name: t('invoiceList.columns.recipient'),
                allowsSorting: true,
                renderer: (_, item) => <RecipientComponent invoice={item} />
            },
            {
                uid: 'created_at',
                name: t('invoiceList.columns.meta'),
                allowsSorting: true,
                renderer: (_, item) => <MetaComponent invoice={item} />
            },
            { name: t('invoiceList.columns.actions'), uid: 'actions', align: 'end' }
        ],
        [showFirstPosition, t]
    );

    const filters: Filter[] = useMemo(
        () => [
            {
                filterKey: 'title',
                filterType: 'text',
                filterName: t('invoiceList.filters.title')
            },
            {
                filterKey: 'items',
                filterType: 'text',
                filterName: t('invoiceList.filters.items')
            },
            {
                filterKey: 'number',
                filterType: 'text',
                filterName: t('invoiceList.filters.number')
            },
            {
                filterKey: 'category',
                filterType: 'text',
                filterName: t('invoiceList.filters.category')
            },
            {
                filterKey: 'created_at',
                filterType: 'date',
                filterName: t('invoiceList.filters.createdAt')
            },
            {
                filterKey: 'date',
                filterType: 'date',
                filterName: t('invoiceList.filters.date')
            },
            {
                filterKey: 'delivery_date',
                filterType: 'date',
                filterName: t('invoiceList.filters.deliveryDate'),
                filterTooltip: t('invoiceList.filters.deliveryDateTooltip')
            },
            {
                filterKey: 'due_date',
                filterType: 'date',
                filterName: t('invoiceList.filters.dueDate')
            },
            {
                filterKey: 'is_finalized',
                filterType: 'select',
                filterName: t('invoiceList.filters.isFinalized'),
                options: [
                    {
                        label: t('invoiceList.filters.options.yes'),
                        value: 'true'
                    },
                    {
                        label: t('invoiceList.filters.options.no'),
                        value: 'false'
                    }
                ]
            },
            {
                filterKey: 'email',
                filterType: 'text',
                filterName: t('invoiceList.filters.email')
            },
            {
                filterKey: 'remarks',
                filterType: 'text',
                filterName: t('invoiceList.filters.remarks')
            },
            {
                filterKey: 'updated_at',
                filterType: 'date',
                filterName: t('invoiceList.filters.updatedAt')
            },
            {
                filterKey: 'last_datev_export',
                filterType: 'date',
                filterName: t('invoiceList.filters.lastDatevExport')
            },
            {
                filterKey: 'datev_export_status',
                filterType: 'select',
                filterName: t('invoiceList.filters.datevExportStatus'),
                options: [
                    {
                        label: t('invoiceList.filters.options.alreadyExported'),
                        value: 'true'
                    },
                    {
                        label: t('invoiceList.filters.options.notYetExported'),
                        value: 'false'
                    }
                ]
            },
            {
                filterKey: 'address_complete',
                filterType: 'select',
                filterName: t('invoiceList.filters.addressComplete'),
                options: [
                    {
                        label: t('invoiceList.filters.options.yes'),
                        value: 'true'
                    },
                    {
                        label: t('invoiceList.filters.options.no'),
                        value: 'false'
                    }
                ]
            },
            {
                filterKey: 'cancelled',
                filterType: 'select',
                filterName: t('invoiceList.filters.cancelled'),
                options: [
                    {
                        label: t('invoiceList.filters.options.yes'),
                        value: 'true'
                    },
                    {
                        label: t('invoiceList.filters.options.no'),
                        value: 'false'
                    }
                ]
            },
            {
                filterKey: 'has_dunnings',
                filterType: 'select',
                filterName: t('invoiceList.filters.hasDunnings'),
                options: [
                    {
                        label: t('invoiceList.filters.options.yes'),
                        value: 'true'
                    },
                    {
                        label: t('invoiceList.filters.options.no'),
                        value: 'false'
                    }
                ]
            },
            {
                filterKey: 'payed_at',
                filterType: 'date',
                filterName: t('invoiceList.filters.payedAt')
            },
            {
                filterKey: 'payment_status',
                filterType: 'select',
                filterName: t('invoiceList.filters.paymentStatus'),
                options: [
                    {
                        label: t('invoiceList.filters.options.paid'),
                        value: 'payed'
                    },
                    {
                        label: t('invoiceList.filters.options.overPaid'),
                        value: 'over_payed'
                    },
                    {
                        label: t('invoiceList.filters.options.overdue'),
                        value: 'over_due'
                    },
                    {
                        label: t('invoiceList.filters.options.notPaid'),
                        value: 'not_payed'
                    }
                ]
            },
            {
                filterKey: 'payment_method',
                filterType: 'select',
                filterName: t('invoiceList.filters.paymentMethod'),
                options: [
                    {
                        label: t('invoiceList.filters.options.cash'),
                        value: 'CASH'
                    },
                    {
                        label: t('invoiceList.filters.options.sepa'),
                        value: 'SEPA'
                    },
                    {
                        label: t('invoiceList.filters.options.transfer'),
                        value: 'TRANSFER'
                    },
                    {
                        label: t('invoiceList.filters.options.online'),
                        value: 'MOLLIE'
                    },
                    {
                        label: t('invoiceList.filters.options.otherMethod'),
                        value: 'OTHER'
                    }
                ]
            },
            {
                filterKey: 'online_payment',
                filterType: 'select',
                filterName: t('invoiceList.filters.onlinePayment'),
                options: [
                    {
                        label: t('invoiceList.filters.options.hasOnlinePayment'),
                        value: 'exists'
                    },
                    {
                        label: t('invoiceList.filters.options.noOnlinePayment'),
                        value: 'none'
                    },
                    {
                        label: t('invoiceList.filters.options.paid'),
                        value: 'paid'
                    },
                    {
                        label: t('invoiceList.filters.options.onlinePending'),
                        value: 'pending'
                    },
                    {
                        label: t('invoiceList.filters.options.onlineFailed'),
                        value: 'failed'
                    }
                ]
            },
            {
                filterKey: 'sepa_xml_requirements_fulfilled',
                filterType: 'select',
                filterName: t('invoiceList.filters.sepaXmlRequirements'),
                options: [
                    {
                        label: t('invoiceList.filters.options.fulfilled'),
                        value: 'true'
                    },
                    {
                        label: t('invoiceList.filters.options.notFulfilled'),
                        value: 'false'
                    }
                ]
            },
            {
                filterKey: 'datev_requirements_fulfilled',
                filterType: 'select',
                filterName: t('invoiceList.filters.datevRequirements'),
                options: [
                    {
                        label: t('invoiceList.filters.options.fulfilled'),
                        value: 'true'
                    },
                    {
                        label: t('invoiceList.filters.options.notFulfilled'),
                        value: 'false'
                    }
                ]
            },
            {
                filterKey: 'sepa_downloaded_at',
                filterType: 'date',
                filterName: t('invoiceList.filters.sepaDownloadedAt')
            },
            {
                filterKey: 'sepa_report',
                filterType: 'select',
                filterName: t('invoiceList.filters.sepaReport'),
                options: [
                    {
                        label: t('invoiceList.filters.options.alreadyCreated'),
                        value: 'true'
                    },
                    {
                        label: t('invoiceList.filters.options.notYetCreated'),
                        value: 'false'
                    }
                ]
            },
            {
                filterKey: 'is_credit',
                filterType: 'select',
                filterName: t('invoiceList.filters.isCredit'),
                options: [
                    {
                        label: t('invoiceList.filters.options.yes'),
                        value: 'true'
                    },
                    {
                        label: t('invoiceList.filters.options.no'),
                        value: 'false'
                    }
                ]
            },
            {
                filterKey: 'sending_status',
                filterType: 'select',
                filterName: t('invoiceList.filters.sendingStatus'),
                options: [
                    {
                        label: t('invoiceList.filters.options.alreadySent'),
                        value: 'true'
                    },
                    {
                        label: t('invoiceList.filters.options.notYetSent'),
                        value: 'false'
                    }
                ]
            },
            {
                filterKey: 'user_invoice_issuer_id',
                filterType: 'select-multiple',
                filterName: t('invoiceList.filters.issuer'),
                options:
                    issuerData?.data?.map((issuer) => ({
                        label: issuer.title,
                        value: issuer.id
                    })) ?? []
            },
            {
                filterKey: 'dunning_level',
                filterType: 'select-multiple',
                filterName: t('invoiceList.filters.dunningLevel'),
                options:
                    Array(5)
                        .fill(true)
                        .map((_, index) => ({
                            label: index + 1,
                            value: index + 1
                        })) ?? []
            },
            {
                filterKey: 'payment_term_id',
                filterType: 'select',
                filterName: t('invoiceList.filters.paymentTerm'),
                options:
                    paymentTerms.data?.data?.map((pt) => ({
                        label: `${pt.key} - ${pt.title}`,
                        value: pt.id
                    })) ?? []
            },
            {
                filterKey: 'email_exists',
                filterType: 'select',
                filterName: t('invoiceList.filters.emailExists'),
                options: [
                    {
                        label: t('invoiceList.filters.options.yes'),
                        value: 'true'
                    },
                    {
                        label: t('invoiceList.filters.options.no'),
                        value: 'false'
                    }
                ]
            },
            {
                filterKey: 'send_at',
                filterType: 'date',
                filterName: t('invoiceList.filters.sendAt')
            },
            {
                filterKey: 'finalized_at',
                filterType: 'date',
                filterName: t('invoiceList.filters.finalizedAt')
            },
            {
                filterKey: 'recipient',
                filterType: 'text',
                filterName: t('invoiceList.filters.recipientName')
            },
            {
                filterKey: 'price',
                filterType: 'numeric',
                filterName: t('invoiceList.filters.price')
            },
            {
                filterKey: 'is_member',
                filterType: 'select',
                filterName: t('invoiceList.filters.isMember'),
                options: [
                    {
                        label: t('invoiceList.filters.options.yes'),
                        value: 'true'
                    },
                    {
                        label: t('invoiceList.filters.options.no'),
                        value: 'false'
                    }
                ]
            }
        ],
        [paymentTerms, issuerData, t]
    );

    const actions: Action[] = archive
        ? [
              {
                  key: 'restore',
                  label: t('invoiceList.actions.restoreMultiple'),
                  action: handleRestoreMultiple,
                  icon: faTrashRestore
              }
          ]
        : [
              {
                  key: 'sepa',
                  label: t('invoiceList.actions.sepaXmlMultiple'),
                  action: handleShowSepaReportAssistent,
                  icon: faReceipt
              },
              // Vorabankündigung ist noch experimentell; das Flag steuert nur die Sichtbarkeit,
              // die API bleibt wie überall rein berechtigungsgesteuert.
              ...(sepaExperimental
                  ? [
                        {
                            key: 'sepa-prenotification',
                            label: t('invoiceList.actions.sepaPrenotification'),
                            action: handleShowSepaPrenotificationAssistent,
                            icon: faEnvelope
                        }
                    ]
                  : []),

              {
                  key: 'export',
                  label: t('invoiceList.actions.export'),
                  action: handleShowExportAssistant,
                  icon: faFileExport
              },
              {
                  key: 'import',
                  label: t('invoiceList.actions.import'),
                  action: handleShowImportAssistant,
                  icon: faFileImport
              },
              {
                  key: 'import-invoice-pdf',
                  label: t('invoiceList.common.importInvoicePdf'),
                  action: handleImportInvoicePdf,
                  icon: faFileImport
              },
              {
                  key: 'capture-payment',
                  label: t('invoiceList.actions.capturePaymentMultiple'),
                  action: handleCapturePaymentMultiple,
                  icon: faCashRegister
              },
              {
                  key: 'reconcile-online-payments',
                  label: t('invoiceList.actions.reconcileOnlinePayments'),
                  action: handleReconcileOnlinePayments,
                  icon: faSync
              },
              {
                  key: 'ship',
                  label: t('invoiceList.actions.shipInvoices'),
                  action: handleShipInvoices,
                  icon: faEnvelope
              },
              {
                  key: 'ship-dunning',
                  label: t('invoiceList.actions.shipDunnings'),
                  action: handleShipMultipleDunnings,
                  icon: faEnvelope,
                  color: 'warning',
                  divider: true
              },
              {
                  key: 'resolve',
                  label: t('invoiceList.common.resolveSelected'),
                  action: handleResolveMultiple,
                  icon: faFileImport
              },
              {
                  key: 'finalize',
                  label: t('invoiceList.actions.finalizeMultiple.label'),
                  action: handleFinalizeMultipleElements,
                  icon: faStamp,
                  divider: true
              },
              {
                  key: 'combine',
                  label: t('invoiceList.actions.combineMultiple.label'),
                  action: handleCombineMultiple,
                  icon: faLink
              },
              {
                  key: 'auto-combine',
                  label: t('invoiceList.actions.autoCombineMultiple'),
                  action: handleAutoCombineInvoices,
                  icon: faLink
              },
              {
                  key: 'rate-payment',
                  label: t('invoiceList.actions.ratePaymentMultiple'),
                  action: handleRatePaymentMultiple,
                  icon: faCalendarAlt
              },
              {
                  key: 'move',
                  label: t('invoiceList.common.moveSelected'),
                  action: handleMoveMultiple,
                  icon: faSignInAlt
              },
              {
                  key: 'duplicate',
                  label: t('invoiceList.actions.duplicateMultiple'),
                  action: handleDuplicateMultipleElements,
                  icon: faCopy
              },
              {
                  key: 'dunning',
                  label: t('invoiceList.actions.dunningMultiple'),
                  action: handleCreateMultipleDunnings,
                  icon: faBell,
                  color: 'warning'
              },
              {
                  key: 'edit',
                  label: t('invoiceList.actions.editMultiple'),
                  action: handleEditMultipleElements,
                  icon: faEdit,
                  color: 'warning'
              },
              {
                  key: 'set-account',
                  label: t('invoiceList.actions.setAccountMultiple'),
                  action: handleSetAccountMultiple,
                  icon: faBookOpen,
                  color: 'warning'
              },
              {
                  key: 'undo-cancel',
                  label: t('invoiceList.actions.undoCancelMultiple.label'),
                  action: handleUndoCancelMultiple,
                  icon: faCheck,
                  color: 'warning'
              },

              {
                  key: 'cancel-payment',
                  label: t('invoiceList.common.cancelPaymentSelected'),
                  action: handleCancelPaymentMultiple,
                  icon: faRectangleXmark,
                  color: 'error',
                  divider: true
              },
              {
                  key: 'cancel',
                  label: t('invoiceList.common.cancelSelected'),
                  action: handleCancelMultiple,
                  icon: faBan,
                  color: 'error'
              },
              {
                  key: 'delete',
                  label: t('invoiceList.actions.deleteMultiple.label'),
                  action: handleDeleteOrArchiveMultiple,
                  icon: faTrash,
                  color: 'error'
              }
          ];

    const rowActions = useCallback(
        (item: UserInvoice) => [
            {
                color: 'secondary',
                icon: faBinoculars,
                tooltip: t('invoiceList.rowActions.showPdf'),
                onPress: () => handleShowPDF(item)
            },
            {
                color: 'secondary',
                icon: faDatabase,
                tooltip: t('invoiceList.rowActions.showFormData'),
                onPress: () => handleOpenFormDataPreview(item),
                show: !!item.form_data_id
            },
            {
                color: 'secondary',
                icon: faCopy,
                tooltip: t('invoiceList.rowActions.duplicate'),
                onPress: () => handleDuplicate(item),
                show: !archive
            },
            {
                color: 'secondary',
                icon: faEnvelope,
                tooltip: t('invoiceList.rowActions.ship'),
                onPress: () => handleShipInvoices({ selection: [String(item.id)] }),
                show: !archive && item.number
            },
            {
                icon: faDollarSign,
                tooltip: t('invoiceList.common.creditInvoice'),
                onPress: () => handleShowInvoiceCreditForm(item),
                show: !archive && !item.isCredit
            },
            {
                icon: faCashRegister,
                color:
                    item.payments_sum_amount > 0 || item.bank_transactions?.length
                        ? 'success'
                        : getSecureMatchSuggestions(item.match_suggestions).length
                          ? 'warning'
                          : 'secondary',
                tooltip:
                    getSecureMatchSuggestions(item.match_suggestions).length &&
                    !item.bank_transactions?.length &&
                    item.payments_sum_amount <= 0 ? (
                        <MatchSuggestionTooltip suggestions={item.match_suggestions} />
                    ) : (
                        t('invoiceList.rowActions.managePayments')
                    ),
                onPress: () => handleManagePayment(item),
                show: !archive && !!item.number
            },
            {
                icon: faReceipt,
                tooltip: t('invoiceList.rowActions.createSepaXml'),
                onPress: () => handleShowSepaReportAssistent({ selection: [String(item.id)] }),
                show: !archive && item.number && !item.cancelled_at && !item.payed_at
            },
            {
                icon: faTrashRestore,
                tooltip: t('invoiceList.rowActions.restore'),
                onPress: () => handleRestoreMultiple({ selection: [item.id] }),
                show: !!archive
            },
            {
                icon: faStamp,
                tooltip: t('invoiceList.rowActions.finalize'),
                onPress: () => handleFinalizeMultipleElements({ selection: [String(item.id)], rowId: String(item.id) }),
                isLoading: isFinalizing && finalizingRowId === String(item.id),
                show: !archive && !item.number
            },
            {
                color: 'warning',
                icon: faBell,
                tooltip: t('invoiceList.common.manageDunnings'),
                onPress: () => handleOpenDunningAssistent(item),
                show: !archive && item.number && !item.cancelled_at
            },
            {
                color: 'warning',
                icon: faEdit,
                tooltip: t('invoiceList.rowActions.edit'),
                onPress: () => handleShowInvoiceForm(item),
                show: !archive
            },
            {
                icon: faBan,
                tooltip: t('invoiceList.rowActions.cancel'),
                color: 'error',
                onPress: () => handleCancelMultiple({ selection: [String(item.id)] }),
                show: !archive && item.number
            },
            {
                color: 'error',
                icon: item.number ? faArchive : faTrash,
                tooltip: item.number ? t('invoiceList.rowActions.archive') : t('invoiceList.rowActions.delete'),
                onPress: () => handleDeleteOrArchive(item),
                show: !archive
            }
        ],
        [archive, finalizingRowId, handleManagePayment, isFinalizing, t]
    );

    return width <= 960 ? (
        <Container center className="tw-h-full">
            <span className="tw-w-1/2 tw-text-center tw-text-accents-6">{t('invoiceList.mobileUnsupported')}</span>
        </Container>
    ) : (
        <div className="tw-flex tw-h-full tw-flex-col tw-gap-2">
            <StatusBar global={global} clubSlug={clubSlug} filter={currentFilter} selection={selection} />
            <DataTable
                className="tw-h-full"
                fetchUrl={
                    fetchUrl ||
                    (archive
                        ? `invoice/${clubSlug}/archive`
                        : `invoice/${clubSlug}${global ? '/all' : path ? '/' + path : ''}`)
                }
                columns={columns}
                actions={actions}
                onFilterChange={setCurrentFilter}
                actionsLoading={isLoading || (isFinalizing && !finalizingRowId)}
                filters={filters}
                smartFilterKeys={QUICK_FILTER_KEYS}
                asyncListRef={asyncListRef}
                tableProps={{
                    onSelectionChange: (keys) => setSelection(keys === 'all' ? keys : (Array.from(keys) as Selection)),
                    showSelectionCheckboxes: true,
                    selectionMode: 'multiple',
                    sticked: true,
                    bordered: true,
                    borderWeight: 'light',
                    compact: false
                }}
                rowActions={(item) => (
                    <TableActionButton.Container justify="flex-end">
                        {rowActions(item)
                            .filter((action) => action.show === undefined || action.show)
                            .map((action, index) => (
                                <TableActionButton
                                    icon={action.icon}
                                    color={
                                        action.color as
                                            | 'default'
                                            | 'primary'
                                            | 'secondary'
                                            | 'success'
                                            | 'warning'
                                            | 'error'
                                            | 'gradient'
                                            | undefined
                                    }
                                    key={index}
                                    onPress={action.onPress}
                                    tooltip={action.tooltip}
                                    isLoading={action.isLoading}
                                />
                            ))}
                    </TableActionButton.Container>
                )}
            />
        </div>
    );
};

export default InvoiceList;
